// "AI across the homebuilding lifecycle", told at a glance: one sentence
// written from the data and two short lines, where AI is live and which
// stages are untapped. The full breakdown (a table of the six stages in order
// and the three supporting functions) is one click away; clicking a row opens
// its work underneath.
import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Lightbulb, X } from "lucide-react";
import { BUSINESS_GROUPS, groupOf, type BusinessGroup, type BusinessGroupId } from "../../data/businessGroups";
import { useBusinessGroups, type BusinessGroupsData, type GroupWork } from "../../hooks/useBusinessGroups";
import type { CatalogItem } from "../../types/navigator";
import { ICONS, STAGE_LABEL, pickMix } from "./LifecycleSection";
import { assetIcon } from "./SideRail";

const STAGES = BUSINESS_GROUPS.filter((g) => g.kind === "stage");
const SUPPORT = BUSINESS_GROUPS.filter((g) => g.kind === "support");
const SHOWN = 6;
const EXPANDED_KEY = "ainav.lifecycle.expanded";

const total = (w: GroupWork) => w.live.length + w.building.length + w.ideas.length;

/** "A", "A and B", "A, B and C" */
function listNames(names: string[]): string {
  return names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The headline: where live AI is, and which stages haven't started. */
function headline(data: BusinessGroupsData): { lead: string; gap: string } {
  const groups = BUSINESS_GROUPS.map((g) => ({ g, w: data.byGroup[g.id] }));
  const live = groups.reduce((n, { w }) => n + w.live.length, 0);
  const top = [...groups].sort((a, b) => b.w.live.length - a.w.live.length)[0];

  let lead: string;
  if (!live) lead = "Nothing is live yet, but the first AI work is underway.";
  else if (top.w.live.length / live >= 0.5)
    lead = `Most live AI sits in ${top.g.short}: ${top.w.live.length} of ${live} solutions.`;
  else lead = `${live} AI solutions are live, led by ${top.g.short}.`;

  const untapped = STAGES.filter((g) => {
    const w = data.byGroup[g.id];
    return !w.live.length && !w.building.length;
  }).map((g) => g.short);
  const gap = untapped.length
    ? `${listNames(untapped)} ${untapped.length === 1 ? "hasn't" : "haven't"} started yet.`
    : "Every stage has AI live or in progress.";
  return { lead, gap };
}

/** A bar of a group's work: live, then in progress, then ideas. Its length is
 *  against the busiest group, so you can compare groups at a glance. */
function WorkBar({ work, scale }: { work: GroupWork; scale: number }) {
  const n = total(work);
  const pct = (k: number) => `${(k / Math.max(1, scale)) * 100}%`;
  return (
    <span className="lc-bar" aria-hidden>
      {n ? (
        <>
          <span className="lc-bar-live" style={{ width: pct(work.live.length) }} />
          <span className="lc-bar-building" style={{ width: pct(work.building.length) }} />
          <span className="lc-bar-idea" style={{ width: pct(work.ideas.length) }} />
        </>
      ) : null}
    </span>
  );
}

/** "1 live, 2 in progress, 3 ideas", for screen readers and tooltips. */
function describe(work: GroupWork): string {
  const parts = [
    work.live.length ? `${work.live.length} live` : "",
    work.building.length ? `${work.building.length} in progress` : "",
    work.ideas.length ? `${work.ideas.length} ${work.ideas.length === 1 ? "idea" : "ideas"}` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "nothing yet";
}

// Regions entered as "Corporate" say who, not where.
const isPlace = (region: string) => region && !/^corporate$/i.test(region.trim());

function Detail({
  group,
  work,
  id,
  onClose,
  onOpen,
}: {
  group: BusinessGroup;
  work: GroupWork;
  id: string;
  onClose: () => void;
  onOpen: (item: CatalogItem) => void;
}) {
  const rows = pickMix(work, SHOWN);
  const tools = work.tools.slice(0, 4);
  const count = total(work);
  const untapped = !work.live.length && !work.building.length;

  return (
    <div id={id} className={`ls-detail db-gt-${group.id}`} role="region" aria-label={group.title}>
      <div className="ls-detail-head">
        <div>
          <h3 className="ls-detail-title">{group.title}</h3>
          <p className="ls-detail-sub">{group.includes}</p>
        </div>
        <button type="button" className="ls-close" onClick={onClose} aria-label="Close">
          <X size={16} aria-hidden />
        </button>
      </div>

      <div className="db-gp">
        <div className={`db-gp-col${tools.length ? "" : " db-gp-col--wide"}`}>
          <p className="db-gp-label">
            <span>Initiatives</span>
          </p>
          {rows.length ? (
            <ul className="db-gp-list">
              {rows.map(([item, stage]) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="db-gp-row"
                    onClick={() => onOpen(item)}
                    aria-label={`${item.title} (initiative, ${STAGE_LABEL[stage]})`}
                  >
                    <span className="db-gp-row-mark">
                      <span className={`db-sd db-sd--${stage}`} title={STAGE_LABEL[stage]} />
                    </span>
                    <span className="db-gp-row-title">{item.title}</span>
                    {isPlace(item.region) ? <span className="db-gp-row-meta">{item.region}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {untapped ? (
            <p className="db-gp-ideas">
              <Lightbulb size={14} aria-hidden />
              <span>Ideas to start with: {group.ideas.join(" · ")}</span>
            </p>
          ) : null}
        </div>
        {tools.length ? (
          <div className="db-gp-col">
            <p className="db-gp-label">
              <span>Tools</span>
            </p>
            <ul className="db-gp-list">
              {tools.map((t) => {
                const Icon = assetIcon(t.assetType);
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      className="db-gp-row"
                      onClick={() => onOpen(t)}
                      aria-label={`${t.title} (${t.assetType || "tool"})`}
                    >
                      <span className="db-gp-row-mark">
                        <Icon size={14} aria-hidden />
                      </span>
                      <span className="db-gp-row-title">{t.title}</span>
                      {t.assetType ? <span className="db-gp-type">{t.assetType}</span> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      <p className="ls-detail-foot">
        {count > SHOWN ? (
          <Link className="db-link db-link--small" to="/initiatives">
            All {count} initiatives
          </Link>
        ) : null}
        <Link className="db-link db-link--small" to="/initiatives?new=1">
          Submit an idea for {group.short} <ArrowRight size={13} aria-hidden />
        </Link>
      </p>
    </div>
  );
}

export function LifecycleStory({
  viewerTeam,
  onOpen,
}: {
  /** The viewer's department, to mark their own group. */
  viewerTeam?: string;
  onOpen: (item: CatalogItem) => void;
}) {
  const { data, error } = useBusinessGroups();
  const mine = groupOf(viewerTeam);
  const [open, setOpen] = useState<BusinessGroupId | null>(null);
  const detailId = useId();
  const tableId = useId();
  // Collapsed by default to keep the dashboard calm; remembered per viewer.
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem(EXPANDED_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_KEY, expanded ? "1" : "0");
    } catch {
      /* not remembered, that's fine */
    }
  }, [expanded]);
  /** Opens the breakdown at one area's row. */
  const show = (id: BusinessGroupId) => {
    setExpanded(true);
    setOpen(id);
  };

  const totals = data
    ? Object.values(data.byGroup).reduce(
        (t, w) => ({ live: t.live + w.live.length, building: t.building + w.building.length, ideas: t.ideas + w.ideas.length }),
        { live: 0, building: 0, ideas: 0 }
      )
    : null;
  const story = data ? headline(data) : null;

  const scale = data ? Math.max(1, ...Object.values(data.byGroup).map(total)) : 1;

  const num = (n: number) => (n ? n : <span className="lc-zero">–</span>);

  const row = (g: BusinessGroup, i: number | null) => {
    const Icon = ICONS[g.id];
    const work = data?.byGroup[g.id];
    const on = open === g.id;
    const untapped = !!work && !work.live.length && !work.building.length;
    return (
      <li key={g.id} className={`lc-row-wrap db-gt-${g.id}${on ? " is-open" : ""}`}>
        <button
          type="button"
          className="lc-row"
          aria-expanded={on}
          aria-controls={on ? detailId : undefined}
          aria-label={`${g.title}: ${work ? describe(work) : "loading"}`}
          disabled={!work}
          onClick={() => setOpen(on ? null : g.id)}
        >
          <span className="lc-step">{i === null ? "" : String(i + 1).padStart(2, "0")}</span>
          <span className="lc-name">
            <Icon className="lc-icon" size={15} aria-hidden />
            <span className="lc-name-text">{g.short}</span>
            {g.id === mine ? <span className="db-you">You</span> : null}
            {untapped ? <span className="lc-untapped">Untapped</span> : null}
          </span>
          {work ? (
            <>
              <span className={`lc-num lc-num--live${work.live.length ? " is-on" : ""}`}>{num(work.live.length)}</span>
              <span className="lc-num">{num(work.building.length)}</span>
              <span className="lc-num">{num(work.ideas.length)}</span>
              <WorkBar work={work} scale={scale} />
            </>
          ) : (
            <span className="lc-loading" aria-hidden />
          )}
          <ChevronDown className="lc-chev" size={15} aria-hidden />
        </button>
        {on && work ? <Detail group={g} work={work} id={detailId} onClose={() => setOpen(null)} onOpen={onOpen} /> : null}
      </li>
    );
  };


  return (
    <section className="db-card ls" aria-labelledby="ls-title">
      <div className="lc-head">
        <h2 id="ls-title" className="db-section-title">
          AI across the homebuilding lifecycle
        </h2>
        {totals ? (
          <p className="lc-totals" aria-label="Across the business">
            <span className="lc-total lc-total--live">
              <b>{totals.live}</b> live
            </span>
            <span className="lc-total lc-total--building">
              <b>{totals.building}</b> in progress
            </span>
            <span className="lc-total lc-total--idea">
              <b>{totals.ideas}</b> ideas
            </span>
          </p>
        ) : null}
      </div>
      {story ? (
        <p className="ls-story">
          {story.lead} {story.gap}{" "}
          <Link className="ls-story-cta" to="/initiatives?new=1">
            Got an idea? <ArrowRight size={14} aria-hidden />
          </Link>
        </p>
      ) : (
        <p className="ls-story ls-story--muted">
          {error ? "Couldn't load AI work right now." : "Where AI is live, in progress and still untapped across the business."}
        </p>
      )}

      {data ? (
        <div className="lc-summary">
          <p className="lc-line">
            <span className="lc-line-label">Live in</span>
            {BUSINESS_GROUPS.filter((g) => data.byGroup[g.id].live.length)
              .sort((a, b) => data.byGroup[b.id].live.length - data.byGroup[a.id].live.length)
              .map((g) => (
                <button key={g.id} type="button" className="lc-pill lc-pill--live" onClick={() => show(g.id)}>
                  {g.short} <b>{data.byGroup[g.id].live.length}</b>
                </button>
              ))}
          </p>
          {STAGES.some((g) => !data.byGroup[g.id].live.length && !data.byGroup[g.id].building.length) ? (
            <p className="lc-line">
              <span className="lc-line-label">Untapped</span>
              {STAGES.filter((g) => !data.byGroup[g.id].live.length && !data.byGroup[g.id].building.length).map((g) => (
                <button key={g.id} type="button" className="lc-pill lc-pill--untapped" onClick={() => show(g.id)}>
                  {g.short}
                </button>
              ))}
            </p>
          ) : null}
          <button
            type="button"
            className="lc-toggle"
            aria-expanded={expanded}
            aria-controls={tableId}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Hide the breakdown" : "See the breakdown"}
            <ChevronDown size={15} aria-hidden className={expanded ? "is-flipped" : ""} />
          </button>
        </div>
      ) : null}

      {expanded ? (
      <div className="lc-table" id={tableId}>
        <div className="lc-cols" aria-hidden>
          <span />
          <span>Lifecycle stage</span>
          <span className="lc-col lc-col--live">Live</span>
          <span className="lc-col lc-col--building">
            <span className="lc-col-long">In progress</span>
            <span className="lc-col-short">Prog.</span>
          </span>
          <span className="lc-col lc-col--idea">Ideas</span>
          <span />
          <span />
        </div>
        <ol className="lc-rows" aria-label="The homebuilding lifecycle, in order">
          {STAGES.map((g, i) => row(g, i))}
        </ol>
        <p className="lc-group-label">Supporting functions</p>
        <ul className="lc-rows" aria-label="Supporting functions">
          {SUPPORT.map((g) => row(g, null))}
        </ul>
        <p className="lc-foot">
          {totals
            ? `${totals.live + totals.building + totals.ideas} active initiatives, not counting closed ones. Click a row to see its work.`
            : null}
        </p>
      </div>
      ) : null}
    </section>
  );
}
